import { CollapsibleSider } from '@/features/layout/_components';
import { useStackSiderClearWhenLocationChange } from '@/features/layout/_hooks/clear-of-sider';
import { type FC, type ReactNode, useMemo } from 'react';
import { useWorkflowStackSiderView } from '../../hooks/stack-sider-view';
import { WorkflowCreateFromTemplateForm } from './create-from-template/WorkflowCreateFromTemplateForm';
import { WorkflowCreateFromTemplateFormHeader } from './create-from-template/WorkflowCreateFromTemplateFormHeader';
import { WorkflowCreateFromTemplateFormProvider } from './create-from-template/WorkflowCreateFromTemplateFormProvider';
import { WorkflowDetails } from './details/WorkflowDetails';
import { WorkflowDetailsHeader } from './details/WorkflowDetailsHeader';
import { WorkflowForm } from './form/WorkflowForm';
import { WorkflowFormHeader } from './form/WorkflowFormHeader';
import { WorkflowFormProvider } from './form/WorkflowFormProvider';
import { WorkflowSiderList } from './list/WorkflowSiderList';
import { WorkflowSiderListHeader } from './list/WorkflowSiderListHeader';

export const WorkflowCollapsibleSider: FC = () => {
  const stackSiderView = useWorkflowStackSiderView();
  useStackSiderClearWhenLocationChange({ siderType: 'workflow' });
  const viewType = stackSiderView.lastItem?.config.viewType;

  const headerRender = useMemo(() => {
    switch (viewType) {
      case 'workflow-list':
        return <WorkflowSiderListHeader />;
      case 'workflow-details':
        return <WorkflowDetailsHeader />;
      case 'workflow-form':
        return <WorkflowFormHeader />;
      case 'create-from-template':
        return <WorkflowCreateFromTemplateFormHeader />;
      default:
        return null;
    }
  }, [viewType]);

  const contentRender = useMemo(() => {
    switch (viewType) {
      case 'workflow-list':
        return <WorkflowSiderList />;
      case 'workflow-details':
        return <WorkflowDetails />;
      case 'workflow-form':
        return <WorkflowForm />;
      case 'create-from-template':
        return <WorkflowCreateFromTemplateForm />;
      default:
        return null;
    }
  }, [viewType]);

  const siderRender: ReactNode = useMemo(() => {
    return (
      <CollapsibleSider
        open={!!stackSiderView.lastItem}
        canBack={stackSiderView.list.length > 1}
        onBack={stackSiderView.pop}
        headerRightContent={headerRender}
      >
        {contentRender}
      </CollapsibleSider>
    );
  }, [stackSiderView, headerRender, contentRender]);

  switch (viewType) {
    case 'workflow-form':
      return <WorkflowFormProvider>{siderRender}</WorkflowFormProvider>;
    case 'create-from-template':
      return (
        <WorkflowCreateFromTemplateFormProvider>
          {siderRender}
        </WorkflowCreateFromTemplateFormProvider>
      );
    default:
      return siderRender;
  }
};
